import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { styled } from '@abyss/web/tools/styled';
import { Divider } from '@abyss/web/ui/Divider';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCoverageType } from '../../hooks/useCoverageType';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { useLagoon } from '../../hooks/useLagoon';
import { Provider } from '../../models/Provider';
import {
  returnInclusionFlag,
  returnSuppressFlag,
} from '../../utils/featureSuppress';
import {
  getCurrentMember,
  getIsCurrentMemberSubscriber,
  isLoggedInMember,
} from '../../utils/user.utils';
import { AdobeConstantsAscensionSchedule } from '../AdobeTagging/adobeConstants';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../AdobeTagging/adobeModalTrackEvent';
import { Constants, LOB, ReverseCoverageTypesCodes } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { mobileOnly, phoneOnly } from '../ConstantsStyles';
import { ScheduleModal } from '../ScheduleModal';
import { getFeatureFlag } from '../Utils';
import { ResponsiveText } from './DataCard.style';
import { SchedulePopover } from './SchedulePopover';

export const ScheduleButton = styled('button', {
  gap: '4px',
  display: 'flex',
  alignItems: 'center',
  dynamic: ({
    cssProps: { enableMapViewHideOption, expandedProviderListView },
  }) => ({
    border: enableMapViewHideOption ? '1px solid $primary1' : '',
    ...(enableMapViewHideOption && {
      height: expandedProviderListView ? '40px' : '32px',
    }),
    borderRadius: enableMapViewHideOption ? '24px' : '',
    padding: enableMapViewHideOption ? '1px 16px' : '',
    gap: enableMapViewHideOption ? '8px' : '4px',
    height: expandedProviderListView && '40px',
    '@screen < $xl': {
      height: expandedProviderListView && '32px',
    },
  }),
  '@screen < $xl': {
    height: '32px',
  },
});

export const scheduleTextStyles = (enableMapViewHideOption: boolean) => ({
  fontSize: enableMapViewHideOption ? '16px !important' : '14.22px !important',
  lineHeight: '16px',
  fontWeight: '600',
  '@screen < $sm': {
    fontSize: '$extraSmallText !important',
    fontWeight: '700',
  },
});

type Props = {
  enableMapViewHideOption?: boolean;
  id: string;
  npi: string;
  handleSearchResultClick(clickSection: string): void;
  phone: string;
  practiceId: string;
  providerName: string;
  sectionType?: string;
  showDirection: any;
  providerId?: string;
  locationId: string;
  isCTADisplayLogic?: boolean;
  isTieredDataCard: boolean;
  isGroupedLocationDrawer?: boolean;
  expandedProviderListView?: boolean;
  practitioner?: Provider;
  isAscensionIndicator?: boolean;
};

export const Schedule = ({
  enableMapViewHideOption,
  id,
  npi,
  handleSearchResultClick,
  phone,
  practiceId,
  providerName,
  locationId,
  sectionType,
  showDirection,
  isCTADisplayLogic = false,
  isTieredDataCard,
  isGroupedLocationDrawer,
  expandedProviderListView = false,
  practitioner,
  isAscensionIndicator,
}: Props) => {
  const [isOpenScheduleModal, setIsOpenScheduleModal] = useState(false);
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const mobileOnlyScreen = useMediaQuery(mobileOnly);
  const coverageType = useCoverageType();
  const featureFlags = useLagoon('feature-flags')();
  const schedulingEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ONLINE_SCHEDULING
  );
  const showScheduleButton: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SCHEDULE_OPTION
  );
  const showScheduleButtonForDpndt: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SCHEDULING_FOR_DEPENDENT
  );
  const ascensionAnalyticsEnabled = React.useMemo(
    () =>
      getFeatureFlag(
        featureFlags,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_ASCENSION_ANALYTICS
      ),
    [featureFlags]
  );

  const isCurrentMemberPreEffective = useCurrentMemberIsPreEffective();
  const isGuestUser = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.IS_GUEST
  );

  const isMemberEligibleToSchedule =
    isGuestUser ||
    (showScheduleButtonForDpndt
      ? isLoggedInMember()
      : getIsCurrentMemberSubscriber());

  const docAsapEnabled = schedulingEnabled && practiceId;

  const handleScheduleButtonClick = () => {
    if (!isCurrentMemberPreEffective) {
      handleSearchResultClick('Schedule');
      const isAscensionAnalyticsEnabled =
        isAscensionIndicator && ascensionAnalyticsEnabled;
      if (isAscensionAnalyticsEnabled) {
        adobeLinkTrackEvent({
          name: AdobeConstantsAscensionSchedule.open_modal,
          location: AdobeConstantsAscensionSchedule.location,
        });
      }
      adobeModalTrackEvent(
        isAscensionAnalyticsEnabled
          ? AdobeConstantsAscensionSchedule.open_modal
          : `schedule a visit:${docAsapEnabled ? 'schedule' : 'call'}`
      );
      setIsOpenScheduleModal(true);
    }
  };
  const { lineOfBusiness } = getCurrentMember();

  const suppressDocAsap = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.SUPPRESS_DOC_ASAP
  );

  const includeDocAsap =
    lineOfBusiness === LOB.CNS &&
    returnInclusionFlag(
      ConstantsLagoon.FEATURE_INCLUSION_FLAGS.INCLUDE_DOC_ASAP
    );
  const isAscensionDocAsapVisible = !includeDocAsap && !isAscensionIndicator;

  if (
    (lineOfBusiness === LOB.CNS
      ? isAscensionDocAsapVisible
      : suppressDocAsap) ||
    !showScheduleButton ||
    !isMemberEligibleToSchedule ||
    [
      ReverseCoverageTypesCodes.DENTAL,
      ReverseCoverageTypesCodes.VISION,
    ].includes(coverageType) ||
    sectionType !== Constants.RESULT_SECTION.PROVIDER
  ) {
    return null;
  }

  const scheduleText = (
    <ResponsiveText
      color={enableMapViewHideOption ? '$primary1' : '$interactive1'}
      css={scheduleTextStyles}
      data-auto-testid={`data-card-calendar-schedule-${sectionType}-${id}`}
      data-testid={`data-card-calendar-schedule-${sectionType}-${id}`}
      fontWeight="$bold"
    >
      {isCTADisplayLogic
        ? t('DATA_CARD.SCHEDULE_ONLINE')
        : t('DATA_CARD.SCHEDULE')}
    </ResponsiveText>
  );

  return (
    <React.Fragment>
      <div style={{ height: 'auto' }}>
        <Divider margin={0} orientation="vertical" width={1} />
      </div>
      {!isCurrentMemberPreEffective ? (
        <ScheduleButton
          cssProps={{ enableMapViewHideOption, expandedProviderListView }}
          data-testid={`data-card-calendar-schedule-${sectionType}`}
          onClick={handleScheduleButtonClick}
        >
          {isGroupedLocationDrawer && mobileOnlyScreen ? null : (
            <IconMaterial
              aria-hidden="true"
              color={enableMapViewHideOption ? '$primary1' : '$interactive2'}
              icon="insert_invitation"
              size={mobileScreen ? 18 : 24}
            />
          )}
          {scheduleText}
        </ScheduleButton>
      ) : (
        <SchedulePopover
          mobileScreen={mobileScreen}
          scheduleText={scheduleText}
        />
      )}
      {isOpenScheduleModal ? (
        <ScheduleModal
          ascensionIndicator={practitioner?.ascensionIndicator}
          ascensionSchedulingLink={practitioner?.ascensionSchedulingLink}
          isOpen={isOpenScheduleModal}
          isTieredDataCard={isTieredDataCard}
          locationId={locationId}
          npi={npi}
          phone={phone}
          practiceId={practiceId}
          providerId={id}
          providerName={providerName}
          sectionType={sectionType}
          setIsOpen={setIsOpenScheduleModal}
          showDirection={showDirection}
        />
      ) : null}
    </React.Fragment>
  );
};
